import { useController, useForm } from 'react-hook-form';
import {
  CourseLine,
  CourseLineCategory,
  CourseLineSubCategory,
} from '@/models/CourseLine';
import { TextInput, ToggleGroup, Select, Button } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import CoverImageInput from '@/components/shared/CoverImageInput';
import { useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

export type CourseLineFormFields = {
  name: string;
  category: CourseLineCategory;
  subcategory: CourseLineSubCategory;
  bannerUrl?: string;
};

type CourseLineFormProps = {
  courseLine?: CourseLine;
  isLoading?: boolean;
  onSubmit: (data: CourseLineFormFields) => void;
};

export default function CourseLineForm({
  courseLine,
  isLoading,
  onSubmit,
}: CourseLineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.form',
  });
  const { t: tCategory } = useTranslation('translation', {
    keyPrefix: 'courseLineCategory',
  });
  const { t: tSubCategory } = useTranslation('translation', {
    keyPrefix: 'courseLineSubCategory',
  });

  const categories = Object.values(CourseLineCategory);
  const subcategories = Object.values(CourseLineSubCategory);

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(() => {
    const bannerUrl =
      typeof courseLine?.bannerImg === 'string'
        ? courseLine.bannerImg
        : undefined;
    return {
      name: courseLine?.name ?? '',
      category: courseLine?.category ?? CourseLineCategory.TECHNOLOGY,
      subcategory: courseLine?.subcategory,
      bannerUrl,
    };
  }, [courseLine]);
  const {
    watch,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CourseLineFormFields>({
    defaultValues,
  });

  const { field: categoryField } = useController({
    name: 'category',
    control,
    rules: {
      required: t('categoryRequired'),
    },
  });

  const { field: bannerUrlField } = useController({
    name: 'bannerUrl',
    control,
  });

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      setHasChanges(false);
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset
        label={t('courseName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('courseNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('courseNameRequired') })}
        />
      </TextInput.Fieldset>
      <Fieldset label={t('courseCover')}>
        <CoverImageInput
          value={bannerUrlField.value}
          onChange={bannerUrlField.onChange}
        />
      </Fieldset>
      <Fieldset label={t('category')} labelAlt={errors?.category?.message}>
        <ToggleGroup
          value={categoryField.value}
          onChange={categoryField.onChange}
        >
          {categories.map(category => (
            <ToggleGroup.Item
              key={category}
              variant="dash"
              color="secondary"
              value={category}
            >
              {tCategory(category)}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
      </Fieldset>
      <Fieldset
        label={t('subcategory')}
        labelAlt={errors?.subcategory?.message}
      >
        <Select
          className="w-full"
          color={!errors?.subcategory?.message ? 'default' : 'error'}
          {...register('subcategory', {
            required: t('subcategoryRequired'),
          })}
        >
          <Select.Option value="" disabled>
            {t('subcategoryPlaceholder')}
          </Select.Option>
          {subcategories.map(subcategory => (
            <Select.Option key={subcategory} value={subcategory}>
              {tSubCategory(subcategory)}
            </Select.Option>
          ))}
        </Select>
      </Fieldset>
      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="px-16"
          disabled={isLoading || !hasChanges}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
